import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const slides = useMemo(() => movies.filter((m) => m.backdrop).slice(0, 5), [movies]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative h-[460px] w-full overflow-hidden md:h-[600px]">
      {slides.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} 伦理影视`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-center px-4 md:px-8">
        <div className="w-full max-w-2xl pb-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-medium text-primary-950">
            <i className="ri-fire-fill text-sm"></i> 本周焦点推荐
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {slides[index].title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-100">
            <span className="flex items-center gap-1 font-semibold text-accent-300">
              <i className="ri-star-fill"></i> {slides[index].rating.toFixed(1)}
            </span>
            <span>{slides[index].year}</span>
            <span>{slides[index].type === "movie" ? "电影" : "电视剧"}</span>
            <span>{slides[index].region}</span>
          </div>
          <p className="mt-4 line-clamp-3 max-w-xl text-sm leading-relaxed text-background-100 md:text-base">
            {slides[index].synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/play/${slides[index].id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-fill text-base"></i> 立即观看
            </Link>
            <Link
              to={`/detail/${slides[index].id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/50 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
            >
              查看详情
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 z-20 flex items-center gap-2 md:right-8">
        <button
          type="button"
          aria-label="上一部"
          onClick={() => go(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-100/50 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => go(1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-100/50 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {slides.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到 ${movie.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-8 bg-accent-400" : "w-3 bg-background-100/60"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}